import React, { useCallback, useEffect, useState } from 'react';
import { Radiation, Download, Search, X } from 'lucide-react';
import { rxAPI } from '../services/api';
import '../styles/RegistroRx.css';

/**
 * RegistroRxCentrale — tutte le righe RX del centro.
 *
 * Per segreteria e amministrazione. Un medico qui non entra nemmeno per
 * vedere le proprie: le trova sulla scheda del paziente, e questa
 * schermata esiste per un altro lavoro — il conto fra la struttura e i
 * professionisti.
 *
 * QUESTI IMPORTI NON SONO DELLA CLINICA
 * Non entrano in fatturazione, non entrano nel controllo di gestione, non
 * generano spettanze. Il totale che si legge qui e' quello che i medici
 * devono alla struttura, non quello che la struttura ha incassato: sono
 * due numeri diversi e non vanno sommati.
 *
 * Il campo del costo prodotto e' modificabile qui in riga, ed e' il posto
 * in cui serve davvero: la segreteria lo compila guardando le confezioni
 * uscite dal magazzino, spesso per molte righe di seguito.
 */

const euro = (cent) => (Number(cent || 0) / 100).toFixed(2).replace('.', ',');
const numero = (testo) => {
    const s = String(testo ?? '').trim().replace(/\s/g, '').replace(',', '.');
    if (s === '') return 0;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
};

const RegistroRxCentrale = () => {
    const [righe, setRighe] = useState([]);
    const [totali, setTotali] = useState(null);
    const [medici, setMedici] = useState([]);
    const [filtri, setFiltri] = useState({ medico: '', paziente: '', dal: '', al: '' });
    const [caricando, setCaricando] = useState(true);
    const [errore, setErrore] = useState(null);

    const carica = useCallback(async () => {
        setCaricando(true);
        try {
            const p = Object.fromEntries(Object.entries(filtri).filter(([, v]) => v));
            const r = await rxAPI.elenco(p);
            setRighe(r.data.data.righe);
            setTotali(r.data.data.totali);
            setErrore(null);
        } catch (e) { setErrore('Non riesco a leggere l’elenco'); }
        finally { setCaricando(false); }
    }, [filtri]);

    useEffect(() => { const t = setTimeout(carica, 250); return () => clearTimeout(t); }, [carica]);
    useEffect(() => {
        rxAPI.mediciAbilitati().then((r) => setMedici(r.data.data.medici)).catch(() => {});
    }, []);

    const salvaCosto = async (riga, valore) => {
        const n = numero(valore);
        if (n === null) return;
        if (Math.round(n * 100) === Number(riga.costo_prodotto_cent)) return;
        try { await rxAPI.modifica(riga.id, { costo_prodotto: n }); carica(); }
        catch (e) { setErrore(e?.response?.data?.message || 'Non riesco a salvare il costo'); }
    };

    const esporta = async () => {
        try {
            const p = Object.fromEntries(Object.entries(filtri).filter(([, v]) => v));
            const r = await rxAPI.esporta(p);
            const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv;charset=utf-8' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `registro-rx-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { setErrore('Non riesco a esportare'); }
    };

    const pulito = Object.values(filtri).every((v) => !v);

    return (
        <div className="rx-pagina">
            <header className="rx-testa-pagina">
                <div>
                    <h1><Radiation size={22} aria-hidden="true" /> Registro RX</h1>
                    <p>
                        Prestazioni che i professionisti fatturano in proprio, con la quota che
                        riconoscono alla struttura e il costo dei prodotti che la clinica ha fornito.
                    </p>
                </div>
                <button className="rx-primario" onClick={esporta} disabled={!righe.length}>
                    <Download size={16} aria-hidden="true" /> Esporta
                </button>
            </header>

            <p className="rx-avvertenza">
                <strong>Questi importi non sono della clinica.</strong> Non entrano in fatturazione,
                non entrano nel controllo di gestione e non generano spettanze. Il totale qui sotto è
                quello che i medici devono alla struttura, non quello che la struttura ha incassato:
                sono due numeri diversi e non si sommano.
                {' '}Il costo dei prodotti si toglie prima di dividere: la percentuale si applica
                a quello che resta, così il materiale lo pagano medico e struttura nella stessa
                proporzione della quota.
            </p>

            {errore && <p className="rx-errore">{errore}</p>}

            <div className="rx-filtri">
                <select value={filtri.medico} onChange={(e) => setFiltri({ ...filtri, medico: e.target.value })}>
                    <option value="">Tutti i medici</option>
                    {medici.map((m) => (
                        <option key={m.id} value={m.id}>
                            {m.cognome} {m.nome}
                            {m.quota_struttura_rx_percentuale !== null ? ` — ${m.quota_struttura_rx_percentuale}%` : ''}
                        </option>
                    ))}
                </select>
                <label className="rx-cerca">
                    <Search size={15} aria-hidden="true" />
                    <input placeholder="ID paziente" value={filtri.paziente}
                           onChange={(e) => setFiltri({ ...filtri, paziente: e.target.value })} />
                </label>
                <label>dal <input type="date" value={filtri.dal}
                                  onChange={(e) => setFiltri({ ...filtri, dal: e.target.value })} /></label>
                <label>al <input type="date" value={filtri.al}
                                 onChange={(e) => setFiltri({ ...filtri, al: e.target.value })} /></label>
                {!pulito && (
                    <button className="rx-testo" onClick={() => setFiltri({ medico: '', paziente: '', dal: '', al: '' })}>
                        <X size={14} aria-hidden="true" /> togli i filtri
                    </button>
                )}
            </div>

            {totali && (
                <div className="rx-totali">
                    <div><span className="rx-n">{totali.righe}</span><span>righe</span></div>
                    <div><span className="rx-n">{euro(totali.importo_cent)} €</span><span>importo fatturato dai medici</span></div>
                    <div><span className="rx-n">{euro(totali.costo_cent)} €</span><span>costo dei prodotti</span></div>
                    <div><span className="rx-n">{euro(totali.importo_cent - totali.costo_cent)} €</span>
                         <span>base da dividere</span></div>
                    {/* I due numeri arrivano sommati dal database, non ricalcolati qui:
                        due posti che fanno lo stesso conto sono due posti che prima o
                        poi non coincidono. */}
                    <div className="rx-spicca">
                        <span className="rx-n">{euro(totali.quota_struttura_cent)} €</span>
                        <span>alla struttura</span>
                    </div>
                    <div className={`rx-spicca ${totali.netto_cent < 0 ? 'rx-negativo' : ''}`}>
                        <span className="rx-n">{euro(totali.netto_cent)} €</span>
                        <span>netto dei medici</span>
                    </div>
                </div>
            )}

            {caricando ? <p className="rx-attesa">Carico…</p> : righe.length === 0
                ? <p className="rx-vuoto">Nessuna riga con questi filtri.</p> : (
                <div className="rx-tabella">
                    <table>
                        <thead>
                            <tr>
                                <th className="rx-data">Data</th>
                                <th>Medico</th>
                                <th>Paziente</th>
                                <th>Prestazione</th>
                                <th className="rx-num">Importo</th>
                                <th className="rx-num">Costo prodotto</th>
                                <th className="rx-num">Base</th>
                                <th className="rx-num">Quota struttura %</th>
                                <th className="rx-num">Alla struttura</th>
                                <th className="rx-num">Netto medico</th>
                                <th>Note</th>
                            </tr>
                        </thead>
                        <tbody>
                            {righe.map((r) => (
                                <tr key={r.id}>
                                    <td className="rx-data">{new Date(r.data).toLocaleDateString('it-IT')}</td>
                                    <td>{r.medico_cognome} {r.medico_nome}</td>
                                    <td>
                                        {r.paziente_cognome} {r.paziente_nome}
                                        <span className="rx-id">{r.id_paziente}</span>
                                    </td>
                                    <td>{r.prestazione || <span className="rx-vuoto-cella">—</span>}</td>
                                    <td className="rx-num">{euro(r.importo_cent)} €</td>
                                    {/* L'unico campo modificabile qui: la segreteria lo compila
                                        guardando le confezioni uscite dal magazzino. Cambiandolo
                                        si rifanno da sole base, quota e netto. */}
                                    <td className="rx-num">
                                        <input inputMode="decimal" defaultValue={euro(r.costo_prodotto_cent)}
                                               onBlur={(e) => salvaCosto(r, e.target.value)} />
                                    </td>
                                    <td className="rx-num rx-calcolato" title="Importo meno il costo del prodotto">
                                        {euro(Number(r.importo_cent) - Number(r.costo_prodotto_cent))} €
                                    </td>
                                    {/* La percentuale e' quella delle impostazioni del
                                        medico, congelata sulla riga quando e' nata. */}
                                    <td className="rx-num rx-calcolato">
                                        {r.quota_struttura_percentuale === null
                                            ? <span className="rx-vuoto-cella">—</span>
                                            : `${r.quota_struttura_percentuale}%`}
                                    </td>
                                    <td className="rx-num">
                                        {r.quota_struttura_percentuale === null
                                            ? <span className="rx-vuoto-cella">—</span>
                                            : `${euro(r.quota_struttura_cent)} €`}
                                    </td>
                                    <td className={`rx-num rx-netto ${Number(r.netto_medico_cent) < 0 ? 'rx-negativo' : ''}`}>
                                        {euro(r.netto_medico_cent)} €
                                    </td>
                                    <td className="rx-note-cella">{r.note}</td>
                                </tr>
                            ))}
                        </tbody>
                        {/* Le due righe finali: cosa resta ai medici e cosa spetta alla
                            struttura. Sono i due numeri per cui il registro esiste. */}
                        <tfoot>
                            <tr className="rx-riga-finale">
                                <td colSpan={8}>Netto dei medici</td>
                                <td colSpan={2} className={`rx-num rx-forte ${totali.netto_cent < 0 ? 'rx-negativo' : ''}`}>
                                    {euro(totali.netto_cent)} €
                                </td>
                            </tr>
                            <tr className="rx-riga-finale">
                                <td colSpan={8}>Alla struttura</td>
                                <td colSpan={2} className="rx-num rx-forte">
                                    {euro(totali.quota_struttura_cent)} €
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}

            {righe.length >= 2000 && (
                <p className="rx-nota">
                    Sono mostrate le prime 2000 righe. Restringi il periodo per vederle tutte.
                </p>
            )}
        </div>
    );
};

export default RegistroRxCentrale;
